import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  GetContactRequest,
  GetContactResponse,
  CreateOrUpdateContactRequest,
  UpdateContactRequest,
  DeleteContactRequest,
  ContactResponse,
  FindContactByPhoneRequest,
  FindContactByPhoneResponse,
  GetContactCardDetailsRequest,
  GetContactCardDetailsResponse,
  GetContactCallsRequest,
  GetContactCallsResponse,
  GetContactAppointmentsRequest,
  GetContactAppointmentsResponse,
  GetContactAccountingRequest,
  GetContactAccountingResponse,
  GetContactListRequest,
  GetContactListResponse,
} from "../../models/Contact.model";
import { environment } from "../../environments";

const API_BASE_URL = environment.apiUrl;

// קבלת אנשי קשר
export const getContacts = createAsyncThunk<
  GetContactResponse,
  GetContactRequest,
  { rejectValue: string }
>("contact/getContacts", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Contact/GetContacts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetContactResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to fetch contacts");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

// יצירת/עדכון איש קשר
export const createOrUpdateContact = createAsyncThunk<
  ContactResponse,
  CreateOrUpdateContactRequest,
  { rejectValue: string }
>("contact/createOrUpdateContact", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}Contact/CreateOrUpdateContact`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: ContactResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to create/update contact");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

// עדכון איש קשר
export const updateContact = createAsyncThunk<
  ContactResponse,
  UpdateContactRequest,
  { rejectValue: string }
>("contact/updateContact", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Contact/UpdateContact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: ContactResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to update contact");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

// מחיקת איש קשר
export const deleteContact = createAsyncThunk<
  ContactResponse,
  DeleteContactRequest,
  { rejectValue: string }
>("contact/deleteContact", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Contact/DeleteContact`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: ContactResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete contact");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});
// מציאת איש קשר לפי טלפון
export const findContactByPhone = createAsyncThunk<
  FindContactByPhoneResponse,
  FindContactByPhoneRequest,
  { rejectValue: string }
>("contact/findContactByPhone", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Contact/FindContactByPhone`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: FindContactByPhoneResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to find contact by phone");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getContactCardDetails = createAsyncThunk<
  GetContactCardDetailsResponse,
  GetContactCardDetailsRequest,
  { rejectValue: string }
>("contact/getContactCardDetails", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}Contact/GetContactCardDetails`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetContactCardDetailsResponse = await response.json();

    if (!data.success)
      return rejectWithValue(
        data.message || "Failed to get contact card details",
      );
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

// קבלת שיחות טלפון של איש קשר
export const getContactCalls = createAsyncThunk<
  GetContactCallsResponse,
  GetContactCallsRequest,
  { rejectValue: string }
>("contact/getContactCalls", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Contact/GetContactCalls`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetContactCallsResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to get contact calls");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

// קבלת פגישות של איש קשר
export const getContactAppointments = createAsyncThunk<
  GetContactAppointmentsResponse,
  GetContactAppointmentsRequest,
  { rejectValue: string }
>(
  "contact/getContactAppointments",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Contact/GetContactAppointments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: GetContactAppointmentsResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to get contact appointments",
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const getContactAccounting = createAsyncThunk<
  GetContactAccountingResponse,
  GetContactAccountingRequest,
  { rejectValue: string }
>("contact/getContactAccounting", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}Contact/GetContactAccounting`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetContactAccountingResponse = await response.json();

    if (!data.success)
      return rejectWithValue(
        data.message || "Failed to get contact accounting",
      );
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getContactList = createAsyncThunk<
  GetContactListResponse,
  GetContactListRequest,
  { rejectValue: string }
>("contact/getContactList", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Contact/GetContactList`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetContactListResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to get contact list");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});
